import * as THREE from 'three';
import { Brush, Evaluator, HOLLOW_SUBTRACTION } from 'three-bvh-csg';
import { addEdgesToMesh, removeEdgesFromMesh } from './visualization';
import { formaStorageService } from '@/services/forma/storage-service';
import { useWindowSettingsStore } from '@/store/windowSettingsStore';
import { snapToGrid } from './geometryModification';

export const originalGeometries = new WeakMap<THREE.Mesh, THREE.BufferGeometry>();
const WINDOWS_STORAGE_KEY = 'windows-storage';

export const buildingWindows = new Map<string, WindowData[]>();

interface WindowData {
	position: THREE.Vector3;
	normal: THREE.Vector3;
	width: number;
	height: number;
	uuid: string;
}

export interface WindowPlacement {
	position: THREE.Vector3;
	normal: THREE.Vector3;
	width: number;
	height: number;
}

function disposeObject(obj: THREE.Object3D): void {
	obj.traverse((child) => {
		if (child instanceof THREE.Mesh) {
			child.geometry.dispose();
			if (Array.isArray(child.material)) {
				child.material.forEach((mat) => mat.dispose());
			} else {
				child.material.dispose();
			}
		}
	});
}

export function getWindowsSize(
	availableHeight: number,
	wallArea: number,
	availableWidth?: number
): { width: number; height: number } {
	const windowSizeUnit = useWindowSettingsStore.getState().windowSizeUnit;
	let holeWidth = 2;
	let holeHeight = 2;

	if (windowSizeUnit === 'percent') {
		const rawPercent = useWindowSettingsStore.getState().windowSizePercent / 100;
		const windowSizePercent = Math.max(0, rawPercent);

		if (
			typeof availableWidth === 'number' &&
			availableWidth > 0 &&
			availableHeight > 0
		) {
			const scale = Math.sqrt(windowSizePercent);
			holeWidth = availableWidth * scale;
			holeHeight = availableHeight * scale;
		} else {
			holeHeight = Math.min(availableHeight, Math.sqrt(windowSizePercent * wallArea));
			holeWidth = holeHeight > 0 ? (windowSizePercent * wallArea) / holeHeight : 0;
		}
	} else {
		const { windowSizeInMeters } = useWindowSettingsStore.getState();
		holeHeight = windowSizeInMeters.height;
		holeWidth = windowSizeInMeters.width;
	}

	return { width: holeWidth, height: holeHeight };
}

export function collectWindowObjects(root: THREE.Object3D): THREE.Object3D[] {
	const result: THREE.Object3D[] = [];
	root.traverse((child) => {
		if (child.userData?.isWindow) {
			result.push(child);
		}
	});
	return result;
}

const getWindowContainer = (root: THREE.Object3D, buildingKey: string): THREE.Group => {
	const name = `__windows__:${buildingKey}`;
	let container = root.getObjectByName(name) as THREE.Group | null;
	if (!container) {
		container = new THREE.Group();
		container.name = name;
		container.userData.isWindowsContainer = true;
		container.userData.parentBuildingKey = buildingKey;
		root.add(container);
	}
	return container;
};

function createWindowObject(
	positionInput: THREE.Vector3 | { x: number; y: number; z: number },
	normalInput: THREE.Vector3 | { x: number; y: number; z: number },
	width: number,
	height: number
): THREE.Group {
	const position =
		positionInput instanceof THREE.Vector3
			? positionInput.clone()
			: new THREE.Vector3(positionInput.x, positionInput.y, positionInput.z);
	const normal =
		normalInput instanceof THREE.Vector3
			? normalInput.clone().normalize()
			: new THREE.Vector3(normalInput.x, normalInput.y, normalInput.z).normalize();

	const windowGroup = new THREE.Group();
	windowGroup.userData.isWindow = true;
	windowGroup.name = 'Window';

	const zUp = new THREE.Vector3(0, 0, 1);
	const tangent = new THREE.Vector3().crossVectors(zUp, normal);
	if (tangent.lengthSq() < 1e-6) {
		tangent.set(1, 0, 0);
	}
	tangent.normalize();

	const bitangent = new THREE.Vector3().crossVectors(normal, tangent).normalize();

	const matrix = new THREE.Matrix4();
	matrix.makeBasis(tangent, bitangent, normal.clone());

	const glassGeometry = new THREE.PlaneGeometry(width, height);
	const glassMaterial = new THREE.MeshBasicMaterial({
		color: 0x88ccff,
		transparent: true,
		opacity: 0.15,
		side: THREE.DoubleSide,
		depthWrite: false,
	});

	const glassPane = new THREE.Mesh(glassGeometry, glassMaterial);
	glassPane.position.set(0, 0, 0.01);

	glassPane.renderOrder = 1000;
	glassPane.userData.isTransparent = true;
	glassPane.userData.category = 'window';

	windowGroup.add(glassPane);

	const frameThickness = 0.1;
	const frameDepth = 0.05;
	const frameMaterial = new THREE.MeshStandardMaterial({
		color: 0x333333,
		metalness: 0.2,
		roughness: 0.8,
	});

	const topFrame = new THREE.Mesh(
		new THREE.BoxGeometry(width + frameThickness * 2, frameThickness, frameDepth),
		frameMaterial
	);
	topFrame.position.set(0, height / 2 + frameThickness / 2, 0);
	windowGroup.add(topFrame);

	const bottomFrame = new THREE.Mesh(
		new THREE.BoxGeometry(width + frameThickness * 2, frameThickness, frameDepth),
		frameMaterial
	);
	bottomFrame.position.set(0, -height / 2 - frameThickness / 2, 0);
	windowGroup.add(bottomFrame);

	const leftFrame = new THREE.Mesh(
		new THREE.BoxGeometry(frameThickness, height, frameDepth),
		frameMaterial
	);
	leftFrame.position.set(-width / 2 - frameThickness / 2, 0, 0);
	windowGroup.add(leftFrame);

	const rightFrame = new THREE.Mesh(
		new THREE.BoxGeometry(frameThickness, height, frameDepth),
		frameMaterial
	);
	rightFrame.position.set(width / 2 + frameThickness / 2, 0, 0);
	windowGroup.add(rightFrame);

	const mullion = new THREE.Mesh(
		new THREE.BoxGeometry(frameThickness / 2, height, frameDepth),
		frameMaterial
	);
	mullion.position.set(0, 0, 0);
	windowGroup.add(mullion);

	windowGroup.applyMatrix4(matrix);

	windowGroup.position.copy(position);
	// No offset needed - window sits flush in the CSG-cut hole

	windowGroup.updateMatrixWorld(true);
	windowGroup.userData.isWindow = true;
	windowGroup.userData.isTransparent = true;

	return windowGroup;
}
export const loadAllBuildingWindows = async (
	buildings: {
		geometry: THREE.BufferGeometry<THREE.NormalBufferAttributes, THREE.BufferGeometryEventMap>;
		mesh: THREE.Mesh;
	}[]
) => {
	const storage = await getWindowsStorage();

	buildings.forEach(async ({ mesh }) => {
		if (mesh.userData.category === 'building') {
			const storedWindows = storage[mesh.userData.path] ?? [];

			const hydratedWindows = storedWindows.map((w) => ({
				uuid: w.uuid,
				width: w.width,
				height: w.height,
				position: new THREE.Vector3(w.position.x, w.position.y, w.position.z),
				normal: new THREE.Vector3(w.normal.x, w.normal.y, w.normal.z),
			})) as WindowData[];

			buildingWindows.set(mesh.userData.path, hydratedWindows);
		}
	});
};

export const loadBuildingWindows = async (building: THREE.Mesh): Promise<void> => {
	if (building.userData.category !== 'building' || !building.userData.path) {
		return;
	}

	const storage = await getWindowsStorage();
	const storedWindows = storage[building.userData.path] ?? [];

	const hydratedWindows = storedWindows.map((w) => ({
		uuid: w.uuid,
		width: w.width,
		height: w.height,
		position: new THREE.Vector3(w.position.x, w.position.y, w.position.z),
		normal: new THREE.Vector3(w.normal.x, w.normal.y, w.normal.z),
	})) as WindowData[];

	buildingWindows.set(building.userData.path, hydratedWindows);
};

export const createRectangularHole = async (
	object: THREE.Mesh,
	point: THREE.Vector3,
	scene: THREE.Scene,
	intersection?: THREE.Intersection,
	onGeometryChanged?: () => void
): Promise<void> => {
	if (!object?.geometry || object.userData.category !== 'building') {
		console.error('Invalid object or geometry');
		return;
	}

	let faceNormal = intersection?.face?.normal?.clone();
	if (faceNormal && object.matrixWorld) {
		const normalMatrix = new THREE.Matrix3().getNormalMatrix(object.matrixWorld);
		faceNormal.applyMatrix3(normalMatrix).normalize();
	} else {
		faceNormal = new THREE.Vector3(0, 0, 1);
	}

	let holeWidth = 2;
	let holeHeight = 2;

	if (intersection?.face && object.geometry) {
		const geom = object.geometry;
		const posAttr = geom.getAttribute('position');
		const idx = intersection.face.a;
		const idy = intersection.face.b;
		const idz = intersection.face.c;
		const va = new THREE.Vector3().fromBufferAttribute(posAttr, idx);
		const vb = new THREE.Vector3().fromBufferAttribute(posAttr, idy);
		const vc = new THREE.Vector3().fromBufferAttribute(posAttr, idz);

		const zUp = new THREE.Vector3(0, 0, 1);
		const tangent = new THREE.Vector3().crossVectors(zUp, faceNormal);
		if (tangent.lengthSq() < 1e-6) {
			tangent.set(1, 0, 0);
		}
		tangent.normalize();
		const bitangent = new THREE.Vector3().crossVectors(faceNormal, tangent).normalize();

		let minLocalX = Infinity;
		let maxLocalX = -Infinity;
		let minLocalY = Infinity;
		let maxLocalY = -Infinity;
		for (const vertex of [va, vb, vc]) {
			const lx = vertex.dot(tangent);
			const ly = vertex.dot(bitangent);
			minLocalX = Math.min(minLocalX, lx);
			maxLocalX = Math.max(maxLocalX, lx);
			minLocalY = Math.min(minLocalY, ly);
			maxLocalY = Math.max(maxLocalY, ly);
		}

		const wallWidth = Math.max(0, maxLocalX - minLocalX);
		const wallHeight = Math.max(0, maxLocalY - minLocalY);
		const wallArea = wallWidth * wallHeight;

		const minZ = Math.min(va.z, vb.z, vc.z);
		const maxZ = Math.max(va.z, vb.z, vc.z);

		const availableHeight = wallHeight > 0 ? wallHeight : maxZ - minZ;
		let centerZ = point.z;

		const halfHeight = availableHeight / 2;
		if (centerZ - halfHeight < minZ) centerZ = minZ + halfHeight;
		if (centerZ + halfHeight > maxZ) centerZ = maxZ - halfHeight;

		const { width, height } = getWindowsSize(availableHeight, wallArea, wallWidth);
		holeHeight = height;
		holeWidth = width;
		point.z = centerZ;
		
		const snappedPoint = snapToGrid(
			point,
			object,
			intersection.face,
			tangent,
			bitangent,
			faceNormal,
			holeWidth,
			holeHeight
		);
		point.copy(snappedPoint);
	}

	if (!originalGeometries.has(object)) {
		originalGeometries.set(object, object.geometry.clone());
	}

	const windowUuid = THREE.MathUtils.generateUUID();
	const windowData: WindowData = {
		position: point.clone(),
		normal: faceNormal.clone(),
		width: holeWidth,
		height: holeHeight,
		uuid: windowUuid,
	};

	const windows = buildingWindows.get(object.userData.path)!;
	windows.push(windowData);

	const serializableWindow: SerializableWindow = {
		uuid: windowData.uuid,
		width: windowData.width,
		height: windowData.height,
		position: { x: windowData.position.x, y: windowData.position.y, z: windowData.position.z },
		normal: { x: windowData.normal.x, y: windowData.normal.y, z: windowData.normal.z },
	};

	const storage = await getWindowsStorage();
	const existing = storage[object.userData.path] ?? [];
	storage[object.userData.path] = [...existing, serializableWindow];
	await setWindowsStorage(storage);

	await rebuildBuildingWithWindows(object, scene, onGeometryChanged);
};

export const createRectangularHolesBatch = async (
	object: THREE.Mesh,
	placements: WindowPlacement[],
	scene: THREE.Scene,
	onGeometryChanged?: () => void
): Promise<void> => {
	if (!object?.geometry || object.userData.category !== 'building' || placements.length === 0) {
		return;
	}

	if (!originalGeometries.has(object)) {
		originalGeometries.set(object, object.geometry.clone());
	}

	const buildingPath = object.userData.path;
	if (!buildingPath) {
		console.error('Building path not found for batch window creation');
		return;
	}

	const windows = buildingWindows.get(buildingPath) ?? [];
	const serializableWindowsToAdd: SerializableWindow[] = [];

	for (const placement of placements) {
		const windowData: WindowData = {
			position: placement.position.clone(),
			normal: placement.normal.clone().normalize(),
			width: placement.width,
			height: placement.height,
			uuid: THREE.MathUtils.generateUUID(),
		};
		windows.push(windowData);
		serializableWindowsToAdd.push({
			uuid: windowData.uuid,
			width: windowData.width,
			height: windowData.height,
			position: {
				x: windowData.position.x,
				y: windowData.position.y,
				z: windowData.position.z,
			},
			normal: {
				x: windowData.normal.x,
				y: windowData.normal.y,
				z: windowData.normal.z,
			},
		});
	}

	buildingWindows.set(buildingPath, windows);

	const storage = await getWindowsStorage();
	const existing = storage[buildingPath] ?? [];
	storage[buildingPath] = [...existing, ...serializableWindowsToAdd];
	await setWindowsStorage(storage);

	await rebuildBuildingWithWindows(object, scene, onGeometryChanged);
};

export const deleteRectangularHole = async (
	windowObject: THREE.Object3D,
	scene: THREE.Scene,
	onGeometryChanged?: () => void
): Promise<void> => {
	let windowGroup: THREE.Group | null = null;

	if (windowObject.userData.isWindow) {
		windowGroup = windowObject as THREE.Group;
	} else {
		let current = windowObject;
		while (current.parent && !current.userData.isWindow) {
			current = current.parent;
		}
		if (current.userData.isWindow) {
			windowGroup = current as THREE.Group;
		}
	}

	if (!windowGroup || !windowGroup.userData.windowUuid) {
		console.warn('No window object found or missing UUID');
		return;
	}

	const windowUuid = windowGroup.userData.windowUuid;

	let buildingMesh: THREE.Mesh | null = null;
	scene.traverse((obj) => {
		if (obj instanceof THREE.Mesh && obj.userData.category === 'building') {
			const windows = buildingWindows.get(obj.userData.path);
			if (windows && windows.some((w) => w.uuid === windowUuid)) {
				buildingMesh = obj;
			}
		}
	});

	if (!buildingMesh) {
		console.warn('Building not found for this window');
		scene.remove(windowGroup);
		disposeObject(windowGroup);
		return;
	}

	await loadBuildingWindows(buildingMesh);
	const windows = buildingWindows.get((buildingMesh as THREE.Mesh).userData.path);
	if (windows) {
		const updatedWindows = windows.filter((w) => w.uuid !== windowUuid);
		buildingWindows.set((buildingMesh as THREE.Mesh).userData.path, updatedWindows);

		const storage = await getWindowsStorage();
		const updatedSerializable = updatedWindows.map((w) => ({
			uuid: w.uuid,
			width: w.width,
			height: w.height,
			position: { x: w.position.x, y: w.position.y, z: w.position.z },
			normal: { x: w.normal.x, y: w.normal.y, z: w.normal.z },
		}));
		storage[(buildingMesh as THREE.Mesh).userData.path] = updatedSerializable;
		await setWindowsStorage(storage);

		await loadBuildingWindows(buildingMesh);
	}

	scene.remove(windowGroup);
	windowGroup.parent?.remove(windowGroup);
	disposeObject(windowGroup);

	await rebuildBuildingWithWindows(buildingMesh, scene, onGeometryChanged);
};

export async function rebuildBuildingWithWindows(
	originalBuilding: THREE.Mesh,
	scene: THREE.Scene,
	onGeometryChanged?: () => void
): Promise<void> {
	const windows = buildingWindows.get(originalBuilding.userData.path);
	const originalGeometry = originalGeometries.get(originalBuilding);

	if (!originalGeometry) {
		console.error('Original geometry not found');
		return;
	}

	const parent = originalBuilding.parent ?? scene;
	const buildingKey = originalBuilding.userData.path ?? originalBuilding.uuid;
	const windowsContainer = getWindowContainer(parent, buildingKey);

	try {
		let currentGeometry = originalGeometry.clone();
		
		for (const windowData of windows || []) {
			const zUp = new THREE.Vector3(0, 0, 1);
			const tangent = new THREE.Vector3().crossVectors(zUp, windowData.normal);
			if (tangent.lengthSq() < 1e-6) {
				tangent.set(1, 0, 0);
			}
			tangent.normalize();

			const bitangent = new THREE.Vector3()
				.crossVectors(windowData.normal, tangent)
				.normalize();

			const matrix = new THREE.Matrix4();
			matrix.makeBasis(tangent, bitangent, windowData.normal);
			matrix.setPosition(windowData.position);

			const preparedGeometry = currentGeometry.clone();

			if (!preparedGeometry.attributes.normal) {
				preparedGeometry.computeVertexNormals();
			}
			if (!preparedGeometry.attributes.uv) {
				const uvCount = preparedGeometry.attributes.position.count;
				const uvs = new Float32Array(uvCount * 2);
				preparedGeometry.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
			}
			if (!preparedGeometry.index) {
				const indices = [];
				for (let i = 0; i < preparedGeometry.attributes.position.count; i++) {
					indices.push(i);
				}
				preparedGeometry.setIndex(indices);
			}

			const brush1 = new Brush(preparedGeometry);
			brush1.position.copy(originalBuilding.position);
			brush1.rotation.copy(originalBuilding.rotation);
			brush1.scale.copy(originalBuilding.scale);
			brush1.updateMatrixWorld();

		// Use sufficient depth for CSG operation to work reliably, even on flat walls
		const csgCutDepth = 1.5;
		const holeGeometry = new THREE.BoxGeometry(
			windowData.width,
			windowData.height,
			csgCutDepth
			);

			if (!holeGeometry.attributes.normal) {
				holeGeometry.computeVertexNormals();
			}
			if (!holeGeometry.attributes.uv) {
				const uvCount = holeGeometry.attributes.position.count;
				const uvs = new Float32Array(uvCount * 2);
				holeGeometry.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
			}

			const brush2 = new Brush(holeGeometry);
			brush2.applyMatrix4(matrix);
			brush2.updateMatrixWorld();

			const evaluator = new Evaluator();
			evaluator.attributes = ['position', 'normal', 'uv'];
			evaluator.useGroups = false;

			const result = evaluator.evaluate(brush1, brush2, HOLLOW_SUBTRACTION);
			if (!result || !result.geometry) {
				console.error('CSG operation failed for window');
				continue;
			}

			if (!result.geometry.index) {
				const indices = [];
				for (let i = 0; i < result.geometry.attributes.position.count; i++) {
					indices.push(i);
				}
				result.geometry.setIndex(indices);
			}
			currentGeometry = result.geometry;
		}

		if (typeof currentGeometry.computeBoundsTree === 'function') {
			currentGeometry.computeBoundsTree();
		}

		currentGeometry.computeBoundingBox();
		currentGeometry.computeBoundingSphere();
		currentGeometry.computeVertexNormals();

		const material = Array.isArray(originalBuilding.material)
			? originalBuilding.material[0].clone()
			: originalBuilding.material.clone();

		if (
			material instanceof THREE.MeshStandardMaterial ||
			material instanceof THREE.MeshBasicMaterial ||
			material instanceof THREE.MeshPhongMaterial
		) {
			material.side = THREE.DoubleSide;
			material.needsUpdate = true;
		}

		const newBuilding = new THREE.Mesh(currentGeometry, material);

		// FIX: Preserve original userData INCLUDING category
		newBuilding.userData = {
			...originalBuilding.userData,
			category: 'building', // Force category to 'building'
			hasHole: windows && windows.length > 0,
			geometryModified: Date.now(),
		};

		newBuilding.castShadow = originalBuilding.castShadow;
		newBuilding.receiveShadow = originalBuilding.receiveShadow;
		newBuilding.name = originalBuilding.name || 'building';

		const originalGeometryRef = originalGeometries.get(originalBuilding);
		if (originalGeometryRef) {
			originalGeometries.set(newBuilding, originalGeometryRef);
		}
		buildingWindows.set(newBuilding.userData.path, windows || []);
		removeEdgesFromMesh(originalBuilding, scene);
		parent.remove(originalBuilding);

		const allWindows = collectWindowObjects(parent);
		const existingWindowsForBuilding = allWindows.filter(
			(window) => window.userData?.parentBuildingPath === originalBuilding.userData.path
		);

		existingWindowsForBuilding.forEach((window) => {
			window.parent?.remove(window);
			disposeObject(window);
		});

		scene.children
			.filter((child) => child.userData.isWindow)
			.forEach((window) => {
				const windowUuids = (windows || []).map((w) => w.uuid);
				if (!windowUuids.includes(window.userData.windowUuid)) {
					scene.remove(window);
					disposeObject(window);
				}
			});

		originalBuilding.geometry.dispose();

		addEdgesToMesh(newBuilding, scene);
		parent.add(newBuilding);
		newBuilding.updateMatrixWorld(true);

		const windowUuids = new Set((windows || []).map((w) => w.uuid));
		const windowsToRemove = collectWindowObjects(parent).filter((child) => {
			if (!child.userData?.isWindow) return false;
			const parentPath =
				child.userData.parentBuildingPath ?? child.userData.parentBuildingUuid;
			return (
				parentPath === newBuilding.userData.path &&
				!windowUuids.has(child.userData.windowUuid)
			);
		});

		windowsToRemove.forEach((window) => {
			window.parent?.remove(window);
			disposeObject(window);
		});

		for (const windowData of windows || []) {
			const existingWindow = collectWindowObjects(parent).find(
				(child) => child.userData?.windowUuid === windowData.uuid
			);
			if (!existingWindow) {
				const windowGroup = createWindowObject(
					windowData.position,
					windowData.normal,
					windowData.width,
					windowData.height
				);
				windowGroup.userData.windowUuid = windowData.uuid;
				windowGroup.userData.parentBuildingKey = buildingKey;
				windowGroup.visible = true;
				windowsContainer.add(windowGroup);
				windowGroup.updateMatrixWorld(true);
			}
		}

		windowsContainer.visible = true;
		windowsContainer.updateMatrixWorld(true);

		originalGeometries.delete(originalBuilding);

		if (onGeometryChanged) {
			setTimeout(() => {
				onGeometryChanged();
			}, 100);
		}
	} catch (error) {
		console.error('Error rebuilding building with windows:', error);
	}
}

type SerializableWindow = {
	uuid: string;
	width: number;
	height: number;
	position: { x: number; y: number; z: number };
	normal: { x: number; y: number; z: number };
};

const getWindowsStorage = async (): Promise<Record<string, SerializableWindow[]>> => {
	return (
		(await formaStorageService.getJSON<Record<string, SerializableWindow[]>>(
			WINDOWS_STORAGE_KEY
		)) ?? {}
	);
};

const setWindowsStorage = async (storage: Record<string, SerializableWindow[]>) => {
	await formaStorageService.setJSON(WINDOWS_STORAGE_KEY, storage);
};
